import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { Button, TextField, Modal, MenuItem } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { setCustomisation } from "../redux/slices/customisationSlice";

import Multiselect from "multiselect-react-dropdown";
import { toast } from "react-toastify";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/EditNote";
import Divider from "@mui/material/Divider";

const useStyles = makeStyles((theme) => ({
  // The Components dropdown paints its options with the brand green via the
  // library's inline `option` style. Inline styles beat the library's own
  // `li:hover` rule, so hover can't repaint the row -- this class restores a
  // visible hover/keyboard-highlight state on top of it.
  componentsDropdown: {
    "& .optionContainer li:hover, & .optionContainer li.highlightOption": {
      background: "#0F2E2A !important",
      color: "#fff !important",
    },
  },
  modalContainer: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxHeight: "70vh",
    overflowY: "auto",
    maxWidth: 400,
    width:"95%",
    backgroundColor: "#fff",
    boxShadow: "0 0 10px rgba(0, 0, 0, 0.5)",
    paddingTop: 10,
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 20,
    outline: "none",
    borderRadius: 5,
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  label: {
    fontWeight: "bold",
  },
  fieldContainer: {
    display: "flex",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1rem",
  },
  buttonSubDiv: {
    display: "flex",
    gap: ".5rem",
  },
  divider: {
    margin: "20px 0",
  },
}));

const EditCustomisationForm = ({
  selectedOption,
  selectedItemId,
  customisationData,
  handleClose,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const curremms = useSelector((state) => state.curremms);
  const headers = useSelector((state) => state.header);
  const emmsheaders = useSelector((state) => state.emmsheader);
  const oldCustomisationData = useSelector((state) => state.customisations);
  const [options, setOptions] = useState([
    "Big Status",
    "Schedule",
    "Nearby Rooms",
    "Manager Booking",
    "ElhiTech Badging",
    "Navigator Room Filtering",
    "Modern Time Input",
    "Banquet",
    "Templates",
    "Kiosk",
    "Navigator Read-Only",
    "Manager Editing",
    "Navigator Self-Admin",
  ]);
  const [userOptions, setUserOptions] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    logoUrl: "",
    components: [],
    backgroundImage: "",
    customProperties: {
      advanced: {
        sicpendpointkey: "emms",
        sicpendpoint: `${curremms.curremms.ipAddress}:${curremms.curremms.portNumber}`,
      },
    },
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");

  const toggleAdvanced = () => {
    setShowAdvanced((prev) => {
      const newShowAdvanced = !prev;
      if (!newShowAdvanced) {
        setEditMode(false);
      }
      return newShowAdvanced;
    });
  };

  const toggleEditMode = () => {
    setEditMode((prev) => !prev);
  };

  const handleCustomPropertyChange = (event) => {
    const { name, value } = event.target;

    setFormData((prevFormData) => ({
      ...prevFormData,
      customProperties: {
        ...prevFormData.customProperties,
        [name]: value,
      },
    }));
  };

  const handleAddCustomProperty = () => {
    if (newKey && newValue) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        customProperties: {
          ...prevFormData.customProperties,
          [newKey]: newValue,
        },
      }));
      setNewKey("");
      setNewValue("");
    }
  };

  useEffect(() => {
    const selectedCustomisation = customisationData.find(
      (customisation) => customisation.id === selectedItemId
    );

    if (selectedCustomisation) {
      setFormData({
        name: selectedCustomisation?.name,
        logoUrl: selectedCustomisation?.logoUrl,
        components: selectedCustomisation?.components,
        backgroundImage: selectedCustomisation?.backgroundImage,
        customProperties: selectedCustomisation?.customProperties,
      });

      setUserOptions(selectedCustomisation.components);
    }
  }, [selectedItemId, customisationData]);

  useEffect(() => {}, [formData]);
  const handleChange = (event) => {
    const { name, value } = event.target;
    const api = localStorage.getItem("api");
    if (name === "sicp") {
      let sicpendpoint = "";
      if (value === "elm") {
        sicpendpoint = `${api}`;
      } else if (value === "emms") {
        sicpendpoint = `${curremms?.curremms?.ipAddress}:${curremms?.curremms?.portNumber}`;
      }

      setFormData((prevFormData) => ({
        ...prevFormData,
        customProperties: {
          ...prevFormData?.customProperties,
          advanced: {
            ...prevFormData?.customProperties?.advanced,
            sicpendpointkey: value,
            sicpendpoint: sicpendpoint,
          },
        },
      }));
    } else if (name === "orientation" || name === "resolution") {
      setFormData((prevFormData) => ({
        ...prevFormData,
        customProperties: {
          ...prevFormData.customProperties,
          [name]: value,
        },
      }));
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const json = {
      name: formData.name,
      logoUrl: formData.logoUrl,
      components: userOptions,
      backgroundImage: formData.backgroundImage,
      customProperties: formData.customProperties,
    };
    const json2 = { ...formData };
    json2.components = json;

    const requestBody = {
      path: "customisation",
      query: {},
      emmsId: `${selectedOption}`,
      body: { ...json, id: selectedItemId },
      requestType: "POST",
      headers: emmsheaders?.emmsheaders?.headers,
    };

    if (
      formData.backgroundImage.length > 0 &&
      !formData.backgroundImage.match(/\.(jpeg|jpg|png)$/i)
    ) {
      toast.error("Couldn't be updated ", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    }
    const api = localStorage.getItem("api");
    axios
      .post(api + `/emms/piggyback`, requestBody, headers)
      .then((response) => {
        const editedCustomisation = response.data[0];
        const index = oldCustomisationData.customisations.findIndex(
          (customisation) => customisation.id === editedCustomisation.id
        );

        if (index !== -1) {
          const updatedCustomisationData = [
            ...oldCustomisationData.customisations,
          ];
          updatedCustomisationData[index] = editedCustomisation;
          dispatch(setCustomisation(updatedCustomisationData));
        } else {
          const updatedCustomisationData = [
            ...oldCustomisationData.customisations,
            editedCustomisation,
          ];
          dispatch(setCustomisation(updatedCustomisationData));
        }
        toast.success("Updated successfully", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        handleClose();
      })
      .catch((error) => {
        toast.error("Couldn't be updated ", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        console.error("Error updating customisation:", error);
      });
  };
  const getLabelWithAsterisk = (labelText, isRequired) => {
    return (
      <div style={{ display: "flex", alignItems: "center", width: "120px" }}>
        <label className={classes.label} htmlFor={labelText}>
          {labelText}
        </label>
        {isRequired && <span style={{ color: "red" }}>*</span>}
      </div>
    );
  };
  return (
    <>
      <Modal open={true} onClose={handleClose}>
        <div className={classes.modalContainer}>
          <form onSubmit={handleSubmit} className={classes.formContainer}>
            <h4>Edit Customisation: {formData.name}</h4>

            {!showAdvanced && (
            <>
            <div className={classes.fieldContainer}>
              {getLabelWithAsterisk("Name:", true)}
              <TextField
                required
                id="name"
                name="name"
                label="Name"
                size="small"
                variant="outlined"
                disabled
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            <div className={classes.fieldContainer}>
              {getLabelWithAsterisk("LogoURL:", true)}
              <TextField
                required
                id="logoUrl"
                name="logoUrl"
                size="small"
                label="Logo URL"
                variant="outlined"
                value={formData.logoUrl}
                onChange={handleChange}
              />
            </div>

            <div className={classes.fieldContainer}>
              {getLabelWithAsterisk("Background Image:", false)}
              <TextField
                id="backgroundImage"
                name="backgroundImage"
                label="Background Image"
                size="small"
                variant="outlined"
                value={formData.backgroundImage}
                onChange={handleChange}
              />
            </div>

            <div className={classes.fieldContainer}>
              {getLabelWithAsterisk("Components:", false)}
              <Multiselect
                className={classes.componentsDropdown}
                id="components"
                label="Components"
                name="components"
                size="small"
                value={formData.components}
                isObject={false}
                onRemove={(event) => setUserOptions(event)}
                onSelect={(event) => setUserOptions(event)}
                options={options}
                selectedValues={userOptions}
                style={{
                  chips: {
                    background: "#19433E",
                  },
                  option: {
                    background: "#19433E",
                    // Without an explicit colour the option text keeps the
                    // library's dark default and is unreadable on the green.
                    color: "#fff",
                  },
                  searchBox: {
                    border: "1px  solid lightgray",

                    width: "25ch",
                    minHeight: "50px",
                  },
                  optionContainer: {
                    maxHeight: "100px",
                    overflowY: "auto",
                  },
                }}
              />
            </div>

            <div className={classes.fieldContainer}>
              {getLabelWithAsterisk("SICP configuration:", true)}
              <TextField
                id="sicp"
                name="sicp"
                size="small"
                variant="outlined"
                value={formData?.customProperties?.advanced?.sicpendpointkey}
                onChange={handleChange}
                select
              >
                <MenuItem value="emms">EMMS</MenuItem>
                <MenuItem value="elm">ELM</MenuItem>
              </TextField>
            </div>

            <div className={classes.fieldContainer}>
              {getLabelWithAsterisk("Orientation:", true)}
              <TextField
                id="orientation"
                name="orientation"
                size="small"
                variant="outlined"
                value={formData?.customProperties?.orientation || ""}
                onChange={handleChange}
                select
              >
                <MenuItem value="landscape">Landscape</MenuItem>
                <MenuItem value="portrait">Portrait</MenuItem>
              </TextField>
            </div>
            <div className={classes.fieldContainer}>
              {getLabelWithAsterisk("Resolution:", true)}
              <TextField
                required
                id="resolution"
                name="resolution"
                size="small"
                label="Resolution (e.g., 1920x1080)"
                variant="outlined"
                value={formData?.customProperties?.resolution || ""}
                onChange={handleChange}
              />
            </div>
            </>
            )}
            <div className={classes.buttonContainer}>
              <Button
                style={{
                  textTransform: "none",
                  marginRight: "4px",
                }}
                variant="outlined"
                color="primary"
                onClick={toggleAdvanced}
                startIcon={showAdvanced ? <ArrowBackIcon /> : null}
              >
                {showAdvanced ? "Back" : "Advanced"}
              </Button>
              {showAdvanced && (
                <Button
                  style={{
                    textTransform: "none",
                    whiteSpace: "nowrap",
                  }}
                  variant="contained"
                  color="primary"
                  onClick={toggleEditMode}
                  endIcon={!editMode ? <EditIcon /> : null}
                >
                  {editMode ? "Save Changes" : "Edit JSON"}
                </Button>
              )}
              {!showAdvanced && (
                <div className={classes.buttonSubDiv}>
                  <Button
                    style={{ textTransform: "none" }}
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="small"
                  >
                    Update
                  </Button>
                  <Button
                    style={{ textTransform: "none" }}
                    variant="outlined"
                    color="error"
                    onClick={handleClose}
                    size="small"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
            {showAdvanced && editMode && (
              <div
                style={{
                  maxWidth: "500px",
                  marginTop: "10px",
                  padding: "10px",
                  border: "1px solid #ccc",
                }}
              >
                <h6>Edit Custom Properties</h6>
                <div style={{ marginBottom: "10px" }}>
                  {Object.keys(formData.customProperties).map((key) => (
                    <div key={key} style={{ margin: "15px 0" }}>
                      <TextField
                        id={key}
                        name={key}
                        label={key}
                        variant="outlined"
                        size="small"
                        value={
                          typeof formData.customProperties[key] === "object"
                            ? JSON.stringify(formData.customProperties[key])
                            : formData.customProperties[key]
                        }
                        onChange={handleCustomPropertyChange}
                      />
                    </div>
                  ))}
                </div>
                {Object.keys(formData.customProperties).length > 0 && (
                  <div className={classes.divider}>
                    <Divider />
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: "10px",
                  }}
                >
                  <TextField
                    id="newKey"
                    name="newKey"
                    label="New Key"
                    variant="outlined"
                    size="small"
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    style={{ marginRight: "10px" }}
                  />
                  <TextField
                    id="newValue"
                    name="newValue"
                    label="New Value"
                    variant="outlined"
                    size="small"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    style={{ marginRight: "10px" }}
                  />
                  <Button
                    style={{ textTransform: "none" }}
                    variant="contained"
                    color="primary"
                    onClick={handleAddCustomProperty}
                  >
                    Add
                  </Button>
                </div>
              </div>
            )}
            {showAdvanced && (
              <div
                style={{
                  maxWidth: "500px",
                  marginTop: "10px",
                  padding: "10px",
                  border: "1px solid #ccc",
                }}
              >
                <h6>Custom Properties</h6>
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                  }}
                >
                  {JSON.stringify(formData.customProperties, null, 2)}
                </pre>
              </div>
            )}
          </form>
        </div>
      </Modal>
    </>
  );
};

export default EditCustomisationForm;
